import { useMemo, useState } from "react";
import { ChartContainer, ChartConfig } from "web-design-system";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { formatAsCrore } from "creso-core/utils";
import { AMOUNT_INPUT_MAX, rangeStepper } from "./config";
import { CalculatorInput } from "./calculator-input";
import { CalculatorResultSummary } from "./result-summary";
import { Disclaimer } from "./disclaimer";
import { CalculatorResultContainer } from "./result-container";
import { CalculatorSnapshotContainer } from "./snapshot-container";
import { CalculatorInputSummary } from "./input-summary";
import { SnapshotButton } from "./snapshot-btn";

const DEFAULTS = {
  totalInvestment: 5_000_000,
  withdrawalPerMonth: 25_000,
  expectedReturn: 12,
  duration: 10,
  waitingPeriod: 1,
  stepUpRate: 5,
};

const chartConfig = {
  balance: {
    label: "Portfolio Balance",
    color: "var(--color-chart-1)",
  },
} satisfies ChartConfig;

interface SWPResult {
  finalBalance: number;
  totalWithdrawn: number;
  chartData: { label: string; balance: number }[];
}

function calculateSWP(
  totalInvestment: number,
  withdrawalPerMonth: number,
  expectedReturn: number,
  duration: number,
  waitingPeriod: number,
  stepUpRate: number
): SWPResult {
  const monthlyRate = (1 + expectedReturn / 100) ** (1 / 12) - 1;

  let corpus = totalInvestment;
  for (let m = 0; m < waitingPeriod * 12; m += 1) {
    corpus *= 1 + monthlyRate;
  }

  let totalWithdrawn = 0;
  let currentMonthlyWithdrawal = withdrawalPerMonth;
  const chartData: { label: string; balance: number }[] = [
    { label: "Yr 0", balance: Math.round(corpus) },
  ];

  for (let year = 1; year <= duration; year += 1) {
    for (let month = 0; month < 12; month += 1) {
      corpus = corpus * (1 + monthlyRate) - currentMonthlyWithdrawal;
      totalWithdrawn += currentMonthlyWithdrawal;
      if (corpus < 0) {
        corpus = 0;
        break;
      }
    }
    currentMonthlyWithdrawal *= 1 + stepUpRate / 100;
    chartData.push({
      label: `Yr ${year}`,
      balance: Math.round(Math.max(0, corpus)),
    });
  }

  return { finalBalance: Math.max(0, corpus), totalWithdrawn, chartData };
}

export function SWPCalculator({
  brandRender,
}: {
  brandRender: () => React.ReactNode;
}) {
  const [totalInvestment, setTotalInvestment] = useState(
    DEFAULTS.totalInvestment
  );
  const [withdrawalPerMonth, setWithdrawalPerMonth] = useState(
    DEFAULTS.withdrawalPerMonth
  );
  const [expectedReturn, setExpectedReturn] = useState(DEFAULTS.expectedReturn);
  const [duration, setDuration] = useState(DEFAULTS.duration);
  const [waitingPeriod, setWaitingPeriod] = useState(DEFAULTS.waitingPeriod);
  const [stepUpRate, setStepUpRate] = useState(DEFAULTS.stepUpRate);

  const { finalBalance, totalWithdrawn, chartData } = useMemo(
    () =>
      calculateSWP(
        totalInvestment,
        withdrawalPerMonth,
        expectedReturn,
        duration,
        waitingPeriod,
        stepUpRate
      ),
    [
      totalInvestment,
      withdrawalPerMonth,
      expectedReturn,
      duration,
      waitingPeriod,
      stepUpRate,
    ]
  );

  const summaryItems = [
    { label: "Total Invested", value: formatAsCrore(totalInvestment) },
    { label: "Total Withdrawn", value: formatAsCrore(totalWithdrawn) },
    {
      label: "Final Balance",
      value: formatAsCrore(finalBalance),
      highlight: true,
    },
  ];

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-6">
        <CalculatorInput
          label="Total Investment"
          value={totalInvestment}
          onChange={setTotalInvestment}
          min={1_00_000}
          max={1_00_00_000}
          inputMax={AMOUNT_INPUT_MAX}
          step={rangeStepper}
          unit="₹"
          formatDisplay={(v) => formatAsCrore(v, 1, true)}
        />
        <CalculatorInput
          label="Waiting Period"
          value={waitingPeriod}
          onChange={setWaitingPeriod}
          min={0}
          max={40}
          step={1}
          unit="yr"
        />
        <CalculatorInput
          label="Withdrawal / Month"
          value={withdrawalPerMonth}
          onChange={setWithdrawalPerMonth}
          min={1_000}
          max={10_00_000}
          inputMax={AMOUNT_INPUT_MAX}
          step={rangeStepper}
          unit="₹"
          formatDisplay={(v) => formatAsCrore(v, 1, true)}
        />
        <CalculatorInput
          label="Expected Returns"
          value={expectedReturn}
          onChange={setExpectedReturn}
          min={1}
          max={50}
          step={0.5}
          unit="%"
        />
        <CalculatorInput
          label="Withdrawal Duration"
          value={duration}
          onChange={setDuration}
          min={1}
          max={40}
          step={1}
          unit="yr"
        />
        <CalculatorInput
          label="Annual Step-up"
          value={stepUpRate}
          onChange={setStepUpRate}
          min={0}
          max={30}
          step={0.5}
          unit="%"
        />
      </div>

      <CalculatorSnapshotContainer
        brandRender={brandRender}
        inputRender={() => (
          <CalculatorInputSummary
            header="SWP calculation"
            fields={[
              {
                label: "Total Investment",
                value: formatAsCrore(totalInvestment),
              },
              {
                label: "Withdrawal / Month",
                value: formatAsCrore(withdrawalPerMonth),
              },
              { label: "Expected Returns", value: `${expectedReturn}%` },
              { label: "Withdrawal Duration", value: `${duration} year(s)` },
              { label: "Waiting Period", value: `${waitingPeriod} year(s)` },
              { label: "Annual Step-up", value: `${stepUpRate}%` },
            ]}
          />
        )}
        resultRender={({ onSnapshot }) => (
          <CalculatorResultContainer>
            <CalculatorResultSummary items={summaryItems} />
            <div className="min-h-85 w-full relative pt-12 mb-2">
              <SnapshotButton onSnapshot={onSnapshot} />
              <div className="flex flex-col h-full gap-3">
                <ChartContainer config={chartConfig} className="flex-1 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={{ left: 0, right: 0, top: 4, bottom: 0 }}
                      barCategoryGap="20%"
                    >
                      <CartesianGrid vertical={false} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={(v) => formatAsCrore(v, 1)}
                        tick={{ fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        width={52}
                      />
                      <Tooltip
                        formatter={(value: number) => [
                          formatAsCrore(value),
                          "Portfolio Balance",
                        ]}
                        labelClassName="font-semibold"
                        contentStyle={{
                          backgroundColor: "var(--card)",
                          border: "none",
                          borderRadius: "0.5rem",
                          boxShadow: "0 0 0.5rem 0 rgba(0, 0, 0, 0.1)",
                        }}
                        cursor={{ fill: "var(--muted)", opacity: 0.3 }}
                      />
                      <Bar
                        dataKey="balance"
                        fill="var(--color-chart-1)"
                        radius={[4, 4, 4, 4]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
                <div className="flex items-center justify-center">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-sm bg-[var(--color-chart-1)]" />
                    <span className="text-xs text-muted-foreground">
                      Portfolio Balance
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <Disclaimer />
          </CalculatorResultContainer>
        )}
      />
    </div>
  );
}
